import { useAuth } from 'src/auth/AuthContext';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Formik, Form, Field, useFormikContext, FormikErrors, FormikTouched } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Checkbox,
  TextField,
  Grid,
  Typography,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Snackbar
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useCreateLocation } from 'src/queries/locations';
import { Merchant } from 'src/interfaces/interfaces';
import { uploadToS3 } from '../utils/s3Upload';
import AddressAutocomplete from './AddressAutocomplete';
import { MerchantType } from '../interfaces/interfaces';
import countries from '../config/countries.json';
import { usePricing } from '../hooks/usePricing';
import { useAvailableCountries } from '../hooks/useAvailableCountries';
import { useAvailableTypes } from '../hooks/useAvailableTypes';

// Reuse existing interfaces from CreateMerchant
interface MerchantForm {
  merchant: {
    name: string;
    location: string;
    logo: File | null;
    typeId: number;
    totalTarget: number;
    streakTarget: number;
    referralCode?: string;
    streaksEnabled: boolean;
    billingEmail: string;
    billingBusinessName: string;
    billingContactName: string;
    billingBusinessRegistrationNumber: string;
    billingTaxNumber: string;
  };
  address: {
    street: string;
    city: string;
    province: string;
    zip: string;
    country: string;
    countryCode: string;
    placeId?: string;
    lat?: number;
    lng?: number;
  };
  operating_hours: Array<{
    dayOfWeek: number;
    isOpen: boolean;
    openTime: string;
    closeTime: string;
  }>;
}

interface MerchantFormProps {
  onComplete: (created: Merchant) => void;
  onFormStateChange?: (isValid: boolean, isDirty: boolean) => void;
  validateForm?: (validateFn: () => void) => void;
  isCoffeeShop: boolean | null;
  onReferralCodeChange?: (referralCode: string) => void;
}

// User-facing fallback for submission failures. Raw server errors are logged
// to the console only — never shown to the merchant.
const GENERIC_CREATE_ERROR =
  "We couldn't create your location. Please try again in a moment — if it keeps happening, contact support.";
const NETWORK_ERROR_MESSAGE =
  'Network error — please check your connection and try again.';

// Helper function to extract all form errors into a flat list
const extractErrorMessages = (
  errors: FormikErrors<MerchantForm>,
  touched: FormikTouched<MerchantForm>,
  showAll: boolean = false
): string[] => {
  const errorMessages: string[] = [];

  // Check merchant fields
  if (errors.merchant) {
    Object.entries(errors.merchant).forEach(([key, value]) => {
      // Show all errors or only touched ones
      if (value && (showAll || (touched.merchant && (touched.merchant as any)[key]))) {
        errorMessages.push(`${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`);
      }
    });
  }

  // Check address fields
  if (errors.address) {
    Object.entries(errors.address).forEach(([key, value]) => {
      // Show all errors or only touched ones
      if (value && (showAll || (touched.address && (touched.address as any)[key]))) {
        errorMessages.push(`Address ${key}: ${value}`);
      }
    });
  }

  return errorMessages;
};

const initialValues: MerchantForm = {
  merchant: {
    name: '',
    location: '',
    logo: null,
    typeId: 1, // Default to coffee shop ID
    totalTarget: 10,
    streakTarget: 4,
    streaksEnabled: true,
    billingEmail: '',
    billingBusinessName: '',
    billingContactName: '',
    billingBusinessRegistrationNumber: '',
    billingTaxNumber: ''
  },
  address: {
    street: '',
    city: '',
    province: '',
    zip: '',
    country: '',
    countryCode: ''
  },
  operating_hours: [
    { dayOfWeek: 0, isOpen: false, openTime: '08:00', closeTime: '17:00' },
    { dayOfWeek: 1, isOpen: true, openTime: '07:30', closeTime: '17:00' },
    { dayOfWeek: 2, isOpen: true, openTime: '07:30', closeTime: '17:00' },
    { dayOfWeek: 3, isOpen: true, openTime: '07:30', closeTime: '17:00' },
    { dayOfWeek: 4, isOpen: true, openTime: '07:30', closeTime: '17:00' },
    { dayOfWeek: 5, isOpen: true, openTime: '07:30', closeTime: '17:00' },
    { dayOfWeek: 6, isOpen: false, openTime: '08:00', closeTime: '17:00' }
  ]
};

// Schema is a function of the available-country list: countryCode must be
// open for registration once the list has loaded. While the list is still
// loading (or its fetch failed) the check passes — the backend gate on
// POST /merchant remains the authority.
const buildValidationSchema = (availableCountryCodes: string[] | null) => Yup.object().shape({
  merchant: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    location: Yup.string().required('Location is required'),
    logo: Yup.mixed().required('Logo is required'),
    typeId: Yup.number().required('Type is required'),
    totalTarget: Yup.number()
      .min(0, 'Must be positive')
      .max(12, 'Must be 12 or less')
      .required('Total target is required'),
    streakTarget: Yup.number()
      .min(0, 'Must be positive')
      .max(5, 'Must be 5 or less')
      .when('streaksEnabled', {
        is: true,
        then: (schema) => schema.required('Streak target is required'),
        otherwise: (schema) => schema.optional()
      }),
    streaksEnabled: Yup.boolean().required('Streaks enabled is required'),
    billingEmail: Yup.string().email('Invalid email').required('Billing email is required'),
    billingBusinessName: Yup.string().required('Billing business name is required'),
    billingContactName: Yup.string().required('Billing contact name is required'),
    billingBusinessRegistrationNumber: Yup.string(),
    billingTaxNumber: Yup.string()
  }),
  address: Yup.object().shape({
    street: Yup.string().required('Street is required'),
    city: Yup.string().required('City is required'),
    province: Yup.string().required('Province is required'),
    zip: Yup.string().required('ZIP code is required'),
    country: Yup.string().required('Country is required'),
    countryCode: Yup.string().test(
      'country-available',
      "Brewprks isn't available in this country yet",
      (value) => !availableCountryCodes || !value || availableCountryCodes.includes(value)
    )
  }),
  operating_hours: Yup.array().of(
    Yup.object().shape({
      dayOfWeek: Yup.number().required('Day of week is required'),
      openTime: Yup.string().required('Open time is required'),
      closeTime: Yup.string().required('Close time is required')
    })
  )
});

// Renders its children only for paid-plan countries (those with a
// CountryPricing row on the backend); free-plan countries have no invoicing.
// Children stay visible while pricing loads — paid is the fail-closed default.
const PaidPlanOnly: React.FC<{ countryCode?: string; children: React.ReactNode }> = ({
  countryCode,
  children
}) => {
  const { pricing } = usePricing(countryCode);
  if (pricing?.is_free === true) return null;
  return <>{children}</>;
};

const AutoUpdateBillingInfo: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<MerchantForm>();
  const user = useAuth();

  useEffect(() => {
    setFieldValue('merchant.billingBusinessName', values.merchant.name);
  }, [values.merchant.name, setFieldValue]);

  useEffect(() => {
    setFieldValue('merchant.billingEmail', user.email);
  }, [user.email, setFieldValue]);

  useEffect(() => {
    setFieldValue('merchant.billingContactName', `${user.firstName} ${user.lastName}`);
  }, [user.firstName, user.lastName, setFieldValue]);

  return null;
};

// Form state monitor component
const FormStateMonitor: React.FC<{ 
  onFormStateChange: (isValid: boolean, isDirty: boolean) => void 
}> = ({
  onFormStateChange
}) => {
  const { isValid, dirty } = useFormikContext();

  useEffect(() => {
    onFormStateChange(isValid, dirty);
  }, [isValid, dirty, onFormStateChange]);

  return null;
};

// Error monitor component to reset error display when form becomes valid
const FormErrorMonitor: React.FC<{
  showAllErrors: boolean;
  setShowAllErrors: (show: boolean) => void;
}> = ({ showAllErrors, setShowAllErrors }) => {
  const { isValid } = useFormikContext();
  
  useEffect(() => {
    if (showAllErrors && isValid) {
      setShowAllErrors(false);
    }
  }, [isValid, showAllErrors, setShowAllErrors]);
  
  return null;
};

// Form validation component to expose validation function to parent
const FormValidation: React.FC<{ validateFormCallback: (validate: () => void) => void }> = ({
  validateFormCallback
}) => {
  const { validateForm, setTouched } = useFormikContext<MerchantForm>();
  
  useEffect(() => {
    const validateFormWrapper = () => {
      validateForm().then((errors: FormikErrors<MerchantForm>) => {
        console.log('Validation errors:', errors); // Debugging log
        // Create a complete touched object for all fields that have errors
        const touchedFields: FormikTouched<MerchantForm> = {
          merchant: {
            name: Boolean(errors.merchant?.name),
            location: Boolean(errors.merchant?.location),
            logo: Boolean(errors.merchant?.logo),
            typeId: Boolean(errors.merchant?.typeId),
            totalTarget: Boolean(errors.merchant?.totalTarget),
            streakTarget: Boolean(errors.merchant?.streakTarget),
            streaksEnabled: Boolean(errors.merchant?.streaksEnabled),
            billingEmail: Boolean(errors.merchant?.billingEmail),
            billingBusinessName: Boolean(errors.merchant?.billingBusinessName),
            billingContactName: Boolean(errors.merchant?.billingContactName),
            billingBusinessRegistrationNumber: Boolean(errors.merchant?.billingBusinessRegistrationNumber),
            billingTaxNumber: Boolean(errors.merchant?.billingTaxNumber)
          },
          address: {
            street: Boolean(errors.address?.street),
            city: Boolean(errors.address?.city),
            province: Boolean(errors.address?.province),
            zip: Boolean(errors.address?.zip),
            country: Boolean(errors.address?.country)
          }
        };
        
        // Mark all fields with errors as touched
        setTouched(touchedFields);
      });
    };
    
    validateFormCallback(validateFormWrapper);
  }, [validateForm, setTouched, validateFormCallback]);
  
  return null;
};

// Referral code monitor component to notify parent of changes
const ReferralCodeMonitor: React.FC<{ 
  onReferralCodeChange: (referralCode: string) => void 
}> = ({ onReferralCodeChange }) => {
  const { values } = useFormikContext<MerchantForm>();

  useEffect(() => {
    onReferralCodeChange(values.merchant.referralCode || '');
  }, [values.merchant.referralCode, onReferralCodeChange]);

  return null;
};

// Add this new component before the EnhancedCreateMerchant component
const MerchantTypeInitializer: React.FC<{
  isCoffeeShop: boolean | null;
  merchantTypes: MerchantType[];
  currentType: string | number;
  setFieldValue: (field: string, value: any) => void;
}> = ({ isCoffeeShop, merchantTypes, currentType, setFieldValue }) => {
  const [hasSetInitialValue, setHasSetInitialValue] = useState(false);
  
  useEffect(() => {
    // Only set initial value if we haven't already set it and conditions are met
    if (hasSetInitialValue) return;
    
    if (isCoffeeShop === true) {
      if (currentType !== 1) {
        setFieldValue('merchant.typeId', 1); // Default to Coffee Shop ID
        setHasSetInitialValue(true);
      }
    } else if (isCoffeeShop === false && merchantTypes.length > 0) {
      // For non-coffee shops, try to default to typeId 2, or first available if 2 is not present
      const preferredTypeId = 2;
      const preferredType = merchantTypes.find(type => type.id === preferredTypeId);
      
      if (preferredType && (currentType === '' || currentType === 1)) {
        // Only set if current type is empty or still set to coffee shop default
        setFieldValue('merchant.typeId', preferredTypeId);
        setHasSetInitialValue(true);
      } else if (!preferredType && merchantTypes[0] && (currentType === '' || currentType === 1)) {
        // If type 2 not found, default to the first in the list
        setFieldValue('merchant.typeId', merchantTypes[0].id);
        setHasSetInitialValue(true);
      } else if (currentType === '' && merchantTypes[0]) {
        // If current type is empty string (initial state) and types are available
        setFieldValue('merchant.typeId', merchantTypes[0].id);
        setHasSetInitialValue(true);
      }
    }
  }, [merchantTypes, isCoffeeShop, setFieldValue, currentType, hasSetInitialValue]);
  
  return null;
};

const EnhancedCreateMerchant: React.FC<MerchantFormProps> = ({
  onComplete,
  onFormStateChange,
  validateForm,
  isCoffeeShop,
  onReferralCodeChange
}) => {
  const user = useAuth();
  const createLocation = useCreateLocation();
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  // Logo uploads to S3 the moment it's picked, not at submit; we hold the
  // returned public URL to hand to createMerchant.
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placeDetails, setPlaceDetails] = useState<google.maps.places.PlaceResult | null>(null);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [formValidity, setFormValidity] = useState({ isValid: false, dirty: false });

  const { countries: availableCountries } = useAvailableCountries();
  const availableCountryCodes = useMemo(
    () => availableCountries?.map((availableCountry) => availableCountry.country_code) ?? null,
    [availableCountries]
  );
  const validationSchema = useMemo(
    () => buildValidationSchema(availableCountryCodes),
    [availableCountryCodes]
  );

  // Business types are only needed when this is not a coffee shop. The
  // auth-readiness check that used to gate the inline effect (`user.loggedIn &&
  // user.id !== -1`) is now the query's `enabled` flag, and the result is cached
  // so reopening the form does not refetch.
  const { merchantTypes, loadingTypes, typeError } = useAvailableTypes(
    isCoffeeShop === false && user.loggedIn && user.id !== -1
  );

  // Initialize form with coffee shop type if isCoffeeShop is true, or first available type if not
  // Memoized: Formik has enableReinitialize, so a new initialValues object on
  // every render makes it reinitialize continuously, which loops the page.
  // Also copy `merchant` — the old shallow spread mutated the module-level
  // defaults.
  const formInitialValues = useMemo(() => {
    const baseValues = { ...initialValues, merchant: { ...initialValues.merchant } };

    if (isCoffeeShop === false) {
      baseValues.merchant.streaksEnabled = false;
      baseValues.merchant.typeId = '' as any; // Set to empty string for non-coffee shops initially
    } else {
      baseValues.merchant.typeId = 1; // Default to coffee shop ID if true
    }

    return baseValues;
  }, [isCoffeeShop]);

  // If we have a form state monitor, also track form validity locally.
  // Memoized (and state updates bail out when nothing changed) because this
  // is a dependency of FormStateMonitor's effect — an unstable identity here
  // re-runs that effect on every render and loops the page.
  const handleInternalFormStateChange = useCallback((isValid: boolean, isDirty: boolean) => {
    setFormValidity((prev) =>
      prev.isValid === isValid && prev.dirty === isDirty ? prev : { isValid, dirty: isDirty }
    );

    // If form becomes valid, hide all errors
    if (isValid) {
      setShowAllErrors(false);
    }

    // Pass state to parent component
    if (onFormStateChange) {
      onFormStateChange(isValid, isDirty);
    }
  }, [onFormStateChange]);

  const getAddressComponent = (
    components: google.maps.GeocoderAddressComponent[],
    type: string,
    shortName: boolean = false
  ): string => {
    const component = components.find(comp => comp.types.includes(type));
    return shortName ? component ? component.short_name : '' : component ? component.long_name : '';
  };

  const convertOpeningHours = (place: google.maps.places.PlaceResult) => {
    const defaultHours = initialValues.operating_hours;

    if (!place.opening_hours?.periods) return defaultHours;

    return defaultHours.map((defaultDay) => {
      const period = place.opening_hours?.periods?.find(
        (p) => p.open?.day === defaultDay.dayOfWeek
      );

      if (!period) return { ...defaultDay, isOpen: false };

      const openTime = period.open?.time || defaultDay.openTime;
      const closeTime = period.close?.time || defaultDay.closeTime;

      const formatTime = (time: string) => {
        return `${time.slice(0, 2)}:${time.slice(2)}`;
      };

      return {
        dayOfWeek: defaultDay.dayOfWeek,
        isOpen: true,
        openTime: formatTime(openTime),
        closeTime: formatTime(closeTime)
      };
    });
  };

  const getSuburbFromPlace = (place: google.maps.places.PlaceResult) => {
    const addressComponents = place.address_components || [];
    const suburb = addressComponents.find(
      component =>
        component.types.includes('sublocality') ||
        component.types.includes('neighborhood') ||
        component.types.includes('administrative_area_level_2')
    );
    return suburb?.long_name || '';
  };

  const handlePlaceSelect = (
    place: google.maps.places.PlaceResult,
    placeId: string | undefined,
    lat: number | undefined,
    lng: number | undefined,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setPlaceDetails(place);

    if (place.name) {
      setFieldValue('merchant.name', place.name);
    }

    const suburb = getSuburbFromPlace(place);
    if (suburb) {
      setFieldValue('merchant.location', suburb);
    }

    if (place.address_components) {
      const addressComponents = place.address_components;
      const streetNumber = getAddressComponent(addressComponents, 'street_number');
      const route = getAddressComponent(addressComponents, 'route');
      const city = getAddressComponent(addressComponents, 'locality') || getAddressComponent(addressComponents, 'postal_town');

      const formattedAddress = {
        street: streetNumber && route ? `${streetNumber} ${route}` : route,
        city: city,
        province: getAddressComponent(addressComponents, 'administrative_area_level_1'),
        zip: getAddressComponent(addressComponents, 'postal_code'),
        country: getAddressComponent(addressComponents, 'country'),
        countryCode: getAddressComponent(addressComponents, 'country', true),
        placeId: placeId,
        lat: lat,
        lng: lng
      };
      setFieldValue('address', formattedAddress);
    }

    const operatingHours = convertOpeningHours(place);
    operatingHours.forEach((hours, index) => {
      setFieldValue(`operating_hours.${index}`, hours);
    });
  };

  // Stable identity — FormValidation's effect depends on this callback, and
  // it triggers a parent setState; a new identity per render loops the page.
  const handleValidateFormCallback = useCallback((validateWrapper: () => void) => {
    if (!validateForm) return;
    validateForm(() => {
      setShowAllErrors(true);
      validateWrapper();
    });
  }, [validateForm]);

  const handleDismissError = () => {
    setError(null);
    createLocation.reset();
  };

  const handleSubmit = async (values: MerchantForm) => {
    try {
      if (isSubmitting) return;

      setIsSubmitting(true);
      setError(null); // Clear any previous errors

      // Turn off showAllErrors during real submission
      setShowAllErrors(false);

      // The logo already uploaded to S3 when it was picked (handleLogoChange).
      // Just guard that it finished and hand the URL to the API.
      if (isLogoUploading) {
        throw new Error('Logo is still uploading. Please wait a moment and try again.');
      }
      if (logoUploadError) {
        throw new Error(logoUploadError);
      }
      if (!logoUrl) {
        throw new Error('Please upload a logo before submitting.');
      }

      // Make sure streaks are disabled for non-coffee shops
      if (isCoffeeShop === false) {
        values.merchant.streaksEnabled = false;
      }

      // Filter operating hours to include only open days
      values.operating_hours = values.operating_hours.filter(
        (hours) => hours.isOpen
      );

      const merchantData = {
        userId: user.id,
        ...values,
        merchant: {
          ...values.merchant,
          logoUrl
        }
      };

      // mutateAsync returns the created location directly, so success is a
      // straight line: no slice to write to, no lifecycle flags to watch, and
      // the record can be handed straight to the caller.
      const created = await createLocation.mutateAsync(merchantData);

      setIsSubmitting(false);
      onComplete(created);
    } catch (error: any) {
      setIsSubmitting(false);
      console.error('Error creating location:', error);
      if (error?.response) {
        // Server responded with an error — details are in the console.
        setError(GENERIC_CREATE_ERROR);
      } else if (error?.request) {
        // Request was made but no response received
        setError(NETWORK_ERROR_MESSAGE);
      } else if (error?.message) {
        // Client-side errors (logo upload guards, rejected submission) carry
        // a message that is already user-friendly.
        setError(error.message);
      } else {
        setError(GENERIC_CREATE_ERROR);
      }
    }
  };

  const handleLogoChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (!event.currentTarget.files || !event.currentTarget.files[0]) {
      return;
    }

    const file = event.currentTarget.files[0];
    setFieldValue('merchant.logo', file);
    setLogoPreview(URL.createObjectURL(file));

    // Upload straight away so the create-location request only carries the
    // resulting URL. A fresh pick invalidates any previously uploaded logo.
    setLogoUrl(null);
    setLogoUploadError(null);
    setIsLogoUploading(true);
    try {
      const uploadedUrl = await uploadToS3(file);
      setLogoUrl(uploadedUrl);
    } catch (uploadError: any) {
      setLogoUploadError(
        `Failed to upload logo: ${uploadError?.message || 'Network error'}`
      );
    } finally {
      setIsLogoUploading(false);
    }
  };

  const handleOperatingHoursChange = (
    index: number,
    field: string,
    value: any,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue(`operating_hours.${index}.${field}`, value);

    if (index === 1 && (field === 'openTime' || field === 'closeTime')) {
      for (let i = 0; i <= 6; i++) {
        if (i !== 1) {
          setFieldValue(`operating_hours.${i}.${field}`, value);
        }
      }
    }
  };


  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const timeOptions = (() => {
    const options = [];
    for (let hour = 0; hour <= 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
        options.push(time);
      }
    }
    return options;
  })();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper style={{ padding: '20px' }}>
        <Typography variant='h4' gutterBottom>
          Create Location
        </Typography>
        {typeError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {typeError}
          </Alert>
        )}
        <Formik
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, setFieldValue, values, isValid, dirty, setTouched, validateForm: formikValidateForm }) => {
            return (
              <Form id='merchant-submit-form'>
                <AutoUpdateBillingInfo />
                {onFormStateChange && <FormStateMonitor onFormStateChange={handleInternalFormStateChange} />}
                <FormErrorMonitor showAllErrors={showAllErrors} setShowAllErrors={setShowAllErrors} />
                {onReferralCodeChange && <ReferralCodeMonitor onReferralCodeChange={onReferralCodeChange} />}
                <MerchantTypeInitializer 
                  isCoffeeShop={isCoffeeShop}
                  merchantTypes={merchantTypes}
                  currentType={values.merchant.typeId}
                  setFieldValue={setFieldValue}
                />
                
                {/* Error Summary */}
                {(showAllErrors || (Object.keys(errors).length > 0 && Object.keys(touched).length > 0)) && (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 3, mt: 2 }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Please fix the following errors:
                    </Typography>
                    <List dense disablePadding>
                      {extractErrorMessages(errors, touched, showAllErrors).map((message, index) => (
                        <ListItem key={index} disableGutters>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <ErrorIcon color="error" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={message} />
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                )}
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant='subtitle1' gutterBottom>
                      Search for your business like you would on Google Maps
                    </Typography>
                    <AddressAutocomplete
                      onSelect={(place, placeId, lat, lng) =>
                        handlePlaceSelect(place, placeId, lat, lng, setFieldValue)
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <input
                        accept='image/*'
                        style={{ display: 'none' }}
                        id='logo-upload'
                        type='file'
                        onChange={(e) => handleLogoChange(e, setFieldValue)}
                      />
                      <label htmlFor='logo-upload'>
                        <Button 
                          variant='contained' 
                          component='span'
                          color={touched.merchant?.logo && errors.merchant?.logo ? "error" : "primary"}
                        >
                          Upload Logo
                        </Button>
                      </label>
                      <Typography variant="body2" color="text.secondary">
                        Upload your business logo
                      </Typography>
                      {isLogoUploading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">
                            Uploading…
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {logoPreview && (
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <img
                          src={logoPreview}
                          alt='Logo preview'
                          style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                        />
                      </Box>
                    )}
                    {logoUploadError && (
                      <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                        {logoUploadError}
                      </Typography>
                    )}
                    {touched.merchant?.logo && errors.merchant?.logo && (
                      <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                        {errors.merchant.logo}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      fullWidth
                      name='merchant.name'
                      label='Location Name'
                      error={touched.merchant?.name && Boolean(errors.merchant?.name)}
                      helperText={touched.merchant?.name && errors.merchant?.name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      fullWidth
                      name='merchant.location'
                      label='Location Suburb (eg. "Green Point")'
                      error={touched.merchant?.location && Boolean(errors.merchant?.location)}
                      helperText={touched.merchant?.location && errors.merchant?.location}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                      Address Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      fullWidth
                      name='address.street'
                      label='Street Address'
                      error={touched.address?.street && Boolean(errors.address?.street)}
                      helperText={touched.address?.street && errors.address?.street}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name='address.city'
                      label='City'
                      error={touched.address?.city && Boolean(errors.address?.city)}
                      helperText={touched.address?.city && errors.address?.city}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name='address.province'
                      label='Province'
                      error={touched.address?.province && Boolean(errors.address?.province)}
                      helperText={touched.address?.province && errors.address?.province}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name='address.zip'
                      label='ZIP Code'
                      error={touched.address?.zip && Boolean(errors.address?.zip)}
                      helperText={touched.address?.zip && errors.address?.zip}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl
                      fullWidth
                      error={(touched.address?.country && Boolean(errors.address?.country)) || Boolean(errors.address?.countryCode)}
                    >
                      <InputLabel>Country</InputLabel>
                      <Field
                        as={Select}
                        name="address.country"
                        label="Country"
                        value={values.address.country}
                        onChange={(e: any) => {
                          const selectedCountry = countries.find(c => c.name === e.target.value);
                          setFieldValue('address.country', e.target.value);
                          if (selectedCountry) {
                            setFieldValue('address.countryCode', selectedCountry.code);
                          }
                        }}
                      >
                        {(availableCountryCodes
                          ? countries.filter((country) => availableCountryCodes.includes(country.code))
                          : countries
                        ).map((country) => (
                          <MenuItem key={country.code} value={country.name}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Field>
                      {touched.address?.country && errors.address?.country && (
                        <Typography color="error" variant="caption" sx={{ ml: 2, mt: 0.5 }}>
                          {errors.address.country}
                        </Typography>
                      )}
                      {errors.address?.countryCode && (
                        <Typography color="error" variant="caption" sx={{ ml: 2, mt: 0.5 }}>
                          {errors.address.countryCode}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                      Business Type & Loyalty Settings
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth error={touched.merchant?.typeId && Boolean(errors.merchant?.typeId)}>
                      <InputLabel>Type</InputLabel>
                      <Field
                        as={Select}
                        name='merchant.typeId'
                        error={touched.merchant?.typeId && Boolean(errors.merchant?.typeId)}
                        label="Type"
                        disabled={loadingTypes}
                      >
                        {isCoffeeShop ? (
                          <MenuItem value={1}>Coffee Shop</MenuItem>
                        ) : loadingTypes ? (
                          <MenuItem value="" disabled>Loading types...</MenuItem>
                        ) : merchantTypes.length > 0 ? (
                          merchantTypes
                            .filter((type) => type.name !== 'COFFEE_SHOP')
                            .map((type) => (
                              <MenuItem key={type.id} value={type.id}>
                                {type.display_name}
                              </MenuItem>
                            ))
                        ) : (
                          <MenuItem value="" disabled>No types available</MenuItem>
                        )}
                      </Field>
                      {touched.merchant?.typeId && errors.merchant?.typeId && (
                        <Typography color="error" variant="caption">
                          {errors.merchant.typeId}
                        </Typography>
                      )}
                      {loadingTypes && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            Loading business types...
                          </Typography>
                        </Box>
                      )}
                    </FormControl>
                  </Grid>

                  {isCoffeeShop !== false && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Field
                            as={Checkbox}
                            name="merchant.streaksEnabled"
                            checked={values.merchant.streaksEnabled}
                          />
                        }
                        label="Enable Streaks"
                      />
                    </Grid>
                  )}

                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name='merchant.totalTarget'
                      label='Total Target'
                      type='number'
                      error={touched.merchant?.totalTarget && Boolean(errors.merchant?.totalTarget)}
                      helperText={touched.merchant?.totalTarget && errors.merchant?.totalTarget}
                    />
                  </Grid>

                  {values.merchant.streaksEnabled && (
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name='merchant.streakTarget'
                        label='Streak Target'
                        type='number'
                        error={touched.merchant?.streakTarget && Boolean(errors.merchant?.streakTarget)}
                        helperText={touched.merchant?.streakTarget && errors.merchant?.streakTarget}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                      Operating Hours
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {values.operating_hours.map((hours, index) => (
                      <Grid
                        container
                        spacing={2}
                        key={index}
                        alignItems='center'
                        style={{ paddingBottom: '15px' }}
                      >
                        <Grid item xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={hours.isOpen}
                                onChange={(e) =>
                                  handleOperatingHoursChange(
                                    index,
                                    'isOpen',
                                    e.target.checked,
                                    setFieldValue
                                  )
                                }
                              />
                            }
                            label={dayNames[hours.dayOfWeek]}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FormControl fullWidth disabled={!hours.isOpen}>
                            <InputLabel>Open</InputLabel>
                            <Select
                              value={hours.openTime}
                              onChange={(e) =>
                                handleOperatingHoursChange(
                                  index,
                                  'openTime',
                                  e.target.value,
                                  setFieldValue
                                )
                              }
                              label="Open"
                            >
                              {timeOptions.map((time) => (
                                <MenuItem key={time} value={time}>
                                  {time}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                          <FormControl fullWidth disabled={!hours.isOpen}>
                            <InputLabel>Close</InputLabel>
                            <Select
                              value={hours.closeTime}
                              onChange={(e) =>
                                handleOperatingHoursChange(
                                  index,
                                  'closeTime',
                                  e.target.value,
                                  setFieldValue
                                )
                              }
                              label="Close"
                            >
                              {timeOptions.map((time) => (
                                <MenuItem key={time} value={time}>
                                  {time}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                      Referral Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      fullWidth
                      name='merchant.referralCode'
                      label='Enter referral code (if any)'
                      error={touched.merchant?.referralCode && Boolean(errors.merchant?.referralCode)}
                      helperText={touched.merchant?.referralCode && errors.merchant?.referralCode}
                    />
                  </Grid>

                  {/* Billing Information — collected for every paid merchant.
                      Free-plan countries (no CountryPricing row) have no
                      invoicing, so we hide these fields.
                      AutoUpdateBillingInfo still auto-populates them in state so
                      validation passes. */}
                  <PaidPlanOnly countryCode={values.address?.countryCode}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                            Billing Information
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            fullWidth
                            name='merchant.billingEmail'
                            label='Billing Email'
                            error={touched.merchant?.billingEmail && Boolean(errors.merchant?.billingEmail)}
                            helperText={touched.merchant?.billingEmail && errors.merchant?.billingEmail}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            fullWidth
                            name='merchant.billingBusinessName'
                            label='Billing Business Name'
                            error={touched.merchant?.billingBusinessName && Boolean(errors.merchant?.billingBusinessName)}
                            helperText={touched.merchant?.billingBusinessName && errors.merchant?.billingBusinessName}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            fullWidth
                            name='merchant.billingContactName'
                            label='Billing Contact Name'
                            error={touched.merchant?.billingContactName && Boolean(errors.merchant?.billingContactName)}
                            helperText={touched.merchant?.billingContactName && errors.merchant?.billingContactName}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            fullWidth
                            name='merchant.billingBusinessRegistrationNumber'
                            label='Business Registration Number'
                            error={touched.merchant?.billingBusinessRegistrationNumber && Boolean(errors.merchant?.billingBusinessRegistrationNumber)}
                            helperText={touched.merchant?.billingBusinessRegistrationNumber && errors.merchant?.billingBusinessRegistrationNumber}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            fullWidth
                            name='merchant.billingTaxNumber'
                            label='Tax Number'
                            error={touched.merchant?.billingTaxNumber && Boolean(errors.merchant?.billingTaxNumber)}
                            helperText={touched.merchant?.billingTaxNumber && errors.merchant?.billingTaxNumber}
                          />
                        </Grid>
                  </PaidPlanOnly>
                </Grid>

                {validateForm && (
                  <FormValidation validateFormCallback={handleValidateFormCallback} />
                )}
              </Form>
            );
          }}
        </Formik>
        {isSubmitting && (
          <Box display="flex" alignItems="center" gap={2} mt={3} p={2} bgcolor="action.hover" borderRadius={1}>
            <CircularProgress size={24} />
            <Typography>Creating store...</Typography>
          </Box>
        )}

        {/* Submission errors surface as a toast; the form data stays intact
            so the user can simply submit again. */}
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={handleDismissError}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={handleDismissError} sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </Paper>
    </LocalizationProvider>
  );
};

export default EnhancedCreateMerchant;